
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import  themen  from 'theme/styles/styled-colors';
import  * as theme  from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "atlas-authorization-model"
    }}>{`Atlas Authorization Model`}</h1>
    <h2 {...{
      "id": "introduction"
    }}>{`Introduction`}</h2>
    <p>{`Atlas is a scalable and extensible set of core foundational governance services – enabling enterprises to effectively and
efficiently meet their compliance requirements within Hadoop and allows integration with the whole enterprise data ecosystem.
Apache Atlas provides open metadata management and governance capabilities for organizations to build a catalog of their
data assets, classify and govern these assets and provide collaboration capabilities around these data assets for data
scientists, analysts and the data governance team.`}</p>
    <p>{`This document covers details of the authorization model supported by Apache Atlas to control access to metadata managed by Atlas.`}</p>
    <h2 {...{
      "id": "authorization-of-access-to-types"
    }}>{`Authorization of access to Types`}</h2>
    <p>{`Apache Atlas provides a type system that allows users to model the metadata objects they would like to manage. The model
is composed of definitions called ‘types’. Apache Atlas type system supports following categories of types:`}</p>
    <ul>
      <li parentName="ul">{`Entity`}</li>
      <li parentName="ul">{`Classification`}</li>
      <li parentName="ul">{`Relationship`}</li>
      <li parentName="ul">{`Struct`}</li>
      <li parentName="ul">{`Enum`}</li>
    </ul>
    <p>{`The authorization model enables control of which users, groups can perform the following operations on types, based on
type names and type categories:`}</p>
    <ul>
      <li parentName="ul">{`create`}</li>
      <li parentName="ul">{`update`}</li>
      <li parentName="ul">{`delete`}</li>
    </ul>
    <p>{`Here are few examples of access controls supported by the model:`}</p>
    <ul>
      <li parentName="ul">{`Admin users can create/update/delete types of all categories`}</li>
      <li parentName="ul">{`Data stewards can create/update/delete classification types`}</li>
      <li parentName="ul">{`Healthcare data stewards can create/update/delete types having names start with “hc”`}</li>
    </ul>
    <h2 {...{
      "id": "authorization-of-access-to-entities"
    }}>{`Authorization of access to Entities`}</h2>
    <p>{`An entity is an instance of an entity-type and such instances represent objects in the real world – for example a table
in Hive, a HDFS file, a Kafka topic. The authorization model enables control of which users, groups can perform the
following operations on entities – based on entity-types, entity-classifications, entity-id:`}</p>
    <ul>
      <li parentName="ul">{`read`}</li>
      <li parentName="ul">{`create`}</li>
      <li parentName="ul">{`update`}</li>
      <li parentName="ul">{`delete`}</li>
      <li parentName="ul">{`read classification`}</li>
      <li parentName="ul">{`add classification`}</li>
      <li parentName="ul">{`update classification`}</li>
      <li parentName="ul">{`remove classification`}</li>
    </ul>
    <p>{`Here are few examples of access controls supported by the model:`}</p>
    <ul>
      <li parentName="ul">{`Admin users can perform all entity operations on entities of all types`}</li>
      <li parentName="ul">{`Data stewards can perform all entity operations, except delete, on entities of all types`}</li>
      <li parentName="ul">{`Data quality admins can add/update/remove DATA_QUALITY classification`}</li>
      <li parentName="ul">{`Users in specific groups can read/update entities with PII classification or its sub-classification`}</li>
      <li parentName="ul">{`Finance users can read/update entities whose ID start with ‘finance’`}</li>
    </ul>
    <h2 {...{
      "id": "authorization-of-admin-operations"
    }}>{`Authorization of Admin operations`}</h2>
    <p>{`The authorization model enables control of which users, groups can perform the following administrative operations:`}</p>
    <ul>
      <li parentName="ul"><em parentName="li">{`import entities`}</em></li>
      <li parentName="ul"><em parentName="li">{`export entities`}</em></li>
    </ul>
    <p>{`Users with above accesses can import/export entities without requiring them to be granted with fine-grained entity level accesses.`}</p>
    <h2 {...{
      "id": "pluggable-authorization"
    }}>{`Pluggable Authorization`}</h2>
    <p>{`Apache Atlas supports a pluggable authorization interface, as shown below, that enable alternate implementations to handle authorizations.`}</p>
    <p>{`The name of the class implementing the authorization interface can be registered with Apache Atlas using configuration `}<inlineCode parentName="p">{`atlas.authorizer.impl`}</inlineCode>{`. When this property is not set, Apache Atlas will use its default implementation in `}<inlineCode parentName="p">{`org.apache.atlas.authorize.simple.AtlasSimpleAuthorizer`}</inlineCode>{`.`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`
   package org.apache.atlas.authorize;
   public interface AtlasAuthorizer {
     void init();
     void cleanUp();

     boolean isAccessAllowed(AtlasAdminAccessRequest request) throws AtlasAuthorizationException;

     boolean isAccessAllowed(AtlasEntityAccessRequest request) throws AtlasAuthorizationException;

     boolean isAccessAllowed(AtlasTypeAccessRequest request) throws AtlasAuthorizationException;
   }`}
    </SyntaxHighlighter>
    <h2 {...{
      "id": "simple-authorizer"
    }}>{`Simple Authorizer`}</h2>
    <p>{`Simple authorizer is the default authorizer implementation included in Apache Atlas. For details of setting up Apache Atlas
to use simple authorizer, please see `}<a parentName="p" {...{
        "href": "#/AtlasSimpleAuthorizer"
      }}>{`Setting up Atlas to use Simple Authorizer`}</a></p>
    <h2 {...{
      "id": "ranger-authorizer"
    }}>{`Ranger Authorizer`}</h2>
    <p>{`To configure Apache Atlas to use authorization implementation provided by Apache Ranger, include the following property
in application.properties config file:`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.authorizer.impl=ranger`}
    </SyntaxHighlighter>
    <p>{`Apache Ranger Authorizer requires configuration files to be setup, for example to specify Apache Ranger admin server URL,
name of the service containing authorization policies, etc. For more details please see, `}<a parentName="p" {...{
        "href": "#/AtlasRangerAuthorizer"
      }}>{`Setting up Atlas to use Ranger Authorizer`}</a>{`.`}</p>
    <h2 {...{
      "id": "none-authorizer"
    }}>{`None authorizer`}</h2>
    <p>{`In addition to the default authorizer, Apache Atlas includes an authorizer that permits all accesses to all users. This authorizer can be useful in test environments and unit tests. To use this authorizer, set the following configuration:`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.authorizer.impl=NONE`}
    </SyntaxHighlighter>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;